import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, RotateCcw, Loader2, Upload } from "lucide-react";
import api from "@/lib/api";
import { TOUR_SHOTS, TOUR_ORDER } from "@/lib/tourShots";

const compressImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1440 / img.width);
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    resolve(c.toDataURL("image/jpeg", 0.65));
  };
  img.onerror = () => reject(new Error("Not a readable image"));
  img.src = URL.createObjectURL(file);
});

export const TourShotsPanel = () => {
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    api.get("/tour-shots").then(({ data }) => setOverrides(data || {})).catch(() => {});
  }, []);

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const data = await compressImage(file);
      await api.put(`/admin/tour-shots/${key}`, { data });
      setOverrides((o) => ({ ...o, [key]: data }));
      toast.success(`"${TOUR_SHOTS[key][1]}" screenshot updated — live in the tour immediately`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(null); }
  };

  const reset = async (key) => {
    setBusy(key);
    try {
      await api.delete(`/admin/tour-shots/${key}`);
      setOverrides((o) => { const n = { ...o }; delete n[key]; return n; });
      toast.success("Reset to the built-in screenshot");
    } catch (e) {
      toast.error("Reset failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="border border-border rounded-xl bg-card p-5 mb-8" data-testid="tour-shots-panel">
      <div className="flex items-center gap-2 mb-1">
        <Camera className="h-4 w-4 text-[#2f6bff]" />
        <h2 className="font-display font-extrabold">Tour screenshots</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">These appear beside each Meet Maestro tour slide. Upload a fresh screenshot anytime the app changes — it goes live instantly, no redeploy needed. You can also ask the Emergent agent to recapture all ten for you.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {TOUR_ORDER.map((key) => {
          const [defaultImg, caption] = TOUR_SHOTS[key];
          const custom = overrides[key];
          return (
            <div key={key} className="border border-border rounded-lg overflow-hidden bg-background" data-testid={`tour-shot-card-${key}`}>
              <img src={custom || defaultImg} alt={caption} className="w-full h-24 object-cover object-top border-b border-border" />
              <div className="p-2">
                <p className="text-[10px] font-bold text-slate-700 leading-tight mb-1.5 line-clamp-2" title={caption}>{caption}</p>
                {custom && <span className="inline-block text-[9px] font-bold text-emerald-600 bg-emerald-50 rounded px-1 mb-1.5">custom</span>}
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" className="hidden" ref={(el) => (fileRefs.current[key] = el)}
                    onChange={(e) => { upload(key, e.target.files?.[0]); e.target.value = ""; }} data-testid={`tour-shot-file-${key}`} />
                  <button type="button" onClick={() => fileRefs.current[key]?.click()} disabled={busy === key}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2f6bff] hover:underline disabled:opacity-50" data-testid={`tour-shot-upload-${key}`}>
                    {busy === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
                  </button>
                  {custom && (
                    <button type="button" onClick={() => reset(key)} disabled={busy === key}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground" data-testid={`tour-shot-reset-${key}`}>
                      <RotateCcw className="h-3 w-3" /> Reset
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TourShotsPanel;
